import { Pill } from './UI';
import { fmtMonthHu } from '../utils';

// Megosztott időszak-választó a Statisztika oldal Kategóriák/Partnerek/Tartozások
// füleihez (2026-08-24, Gábor kérése). A 2026-07-19-i UX-review már jelezte:
// "3 period pickers with 3 looks → one <PeriodPicker>" — ez az az egy.
//
// Két preset-készlet van, mert a havi bontás blokknak más az alapértelmezettje
// és más az opciói (nincs hónap-szintű bontás, mert épp hónaponként bont):
//   STANDARD_PRESETS     — Aktuális hónap / Előző hónap / Aktuális év / Előző év / Egyéni
//   MONTHLY_TREND_PRESETS — Utolsó 12 hónap / Aktuális év / Előző év / Egyéni

const pad = n => String(n).padStart(2, '0');
const ymd = (y, m, d) => `${y}-${pad(m)}-${pad(d)}`;
const lastDayOfMonth = (y, m) => new Date(y, m, 0).getDate();

export const STANDARD_PRESETS = [
  { value: 'this-month', label: 'Aktuális hónap' },
  { value: 'last-month', label: 'Előző hónap' },
  { value: 'this-year',  label: 'Aktuális év' },
  { value: 'last-year',  label: 'Előző év' },
  { value: 'custom',     label: 'Egyéni' },
];

export const MONTHLY_TREND_PRESETS = [
  { value: 'last-12-months', label: 'Utolsó 12 hónap' },
  { value: 'this-year',      label: 'Aktuális év' },
  { value: 'last-year',      label: 'Előző év' },
  { value: 'custom',         label: 'Egyéni' },
];

/** preset + egyéni {from,to} → {date_from, date_to} (a backend query-paramjai). */
export function periodToRange(preset, custom) {
  const now = new Date();
  const y = now.getFullYear(), m = now.getMonth();   // m: 0-alapú
  switch (preset) {
    case 'this-month':
      return { date_from: ymd(y, m + 1, 1), date_to: ymd(y, m + 1, lastDayOfMonth(y, m + 1)) };
    case 'last-month': {
      const py = m === 0 ? y - 1 : y, pm0 = m === 0 ? 11 : m - 1;   // pm0: 0-alapú
      return { date_from: ymd(py, pm0 + 1, 1), date_to: ymd(py, pm0 + 1, lastDayOfMonth(py, pm0 + 1)) };
    }
    case 'this-year':
      return { date_from: `${y}-01-01`, date_to: `${y}-12-31` };
    case 'last-year':
      return { date_from: `${y - 1}-01-01`, date_to: `${y - 1}-12-31` };
    case 'last-12-months': {
      // az aktuális hónapot IS beleértve, tehát 11 hónappal korábbi hónap 1-jétől.
      const start = new Date(y, m - 11, 1);
      return { date_from: ymd(start.getFullYear(), start.getMonth() + 1, 1),
               date_to: ymd(y, m + 1, lastDayOfMonth(y, m + 1)) };
    }
    case 'custom':
      return {
        ...(custom?.from ? { date_from: custom.from } : {}),
        ...(custom?.to   ? { date_to:   custom.to   } : {}),
      };
    default:
      return {};
  }
}

// ── Projekt-P&L: HÓNAP-tengely (F0-8, INV-11) ─────────────────────────────────
// A `/projects/pnl` `period_from`/`period_to` = ÉÉÉÉ-HH paramétert vár (teljesítési hónap),
// nem napot. ⚠ Az „Előző hónap” januárban a TAVALYI december — a hónapot mindig
// `new Date(y, m - 1, 1)`-ből számoljuk (év-átfordulással), soha nem `m - 1` stringből
// (a régi `periodButtons()` januárban `YYYY-00-01`-et küldött, K-8).
export const PNL_PRESETS = [
  { value: 'this-month', label: 'Aktuális hónap' },
  { value: 'last-month', label: 'Előző hónap' },
  { value: 'q1',         label: 'Q1' },
  { value: 'q2',         label: 'Q2' },
  { value: 'q3',         label: 'Q3' },
  { value: 'q4',         label: 'Q4' },
  { value: 'this-year',  label: 'Aktuális év' },
  { value: 'last-year',  label: 'Előző év' },
  { value: 'custom',     label: 'Egyéni' },
];

const ym = (y, m1) => `${y}-${pad(m1)}`;           // m1: 1-alapú

/** preset + egyéni {from,to} (ÉÉÉÉ-HH) → {period_from, period_to}; `now` a teszthez. */
export function presetToMonths(preset, custom, now = new Date()) {
  const y = now.getFullYear(), m = now.getMonth();   // m: 0-alapú
  const q = (n) => ({ period_from: ym(y, 3 * n - 2), period_to: ym(y, 3 * n) });
  switch (preset) {
    case 'this-month': return { period_from: ym(y, m + 1), period_to: ym(y, m + 1) };
    case 'last-month': {
      const p = new Date(y, m - 1, 1);
      const k = ym(p.getFullYear(), p.getMonth() + 1);
      return { period_from: k, period_to: k };
    }
    case 'q1': return q(1);
    case 'q2': return q(2);
    case 'q3': return q(3);
    case 'q4': return q(4);
    case 'this-year': return { period_from: ym(y, 1),     period_to: ym(y, 12) };
    case 'last-year': return { period_from: ym(y - 1, 1), period_to: ym(y - 1, 12) };
    case 'custom':
      return (custom?.from && custom?.to) ? { period_from: custom.from, period_to: custom.to } : {};
    default: return {};
  }
}

/** A hónap-választó kínálata: az elmúlt 36 hónap + a következő 12 (D41 — nem natív dátummező). */
export function monthOptions(now = new Date()) {
  const out = [];
  for (let i = 12; i >= -36; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const k = ym(d.getFullYear(), d.getMonth() + 1);
    out.push({ value: k, label: fmtMonthHu(k) });
  }
  return out;
}

const dateInputS = {
  background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
  padding: '0.22rem 0.45rem', color: 'var(--text)', fontSize: '0.74rem',
};

export function PeriodFilter({ preset, onPresetChange, custom, onCustomChange,
    presets = STANDARD_PRESETS, monthPicker = false }) {
  if (monthPicker) {
    const opts = monthOptions();
    const sel = (key, label) => (
      <select aria-label={label} value={custom?.[key] || ''} style={dateInputS}
        onChange={e => onCustomChange({ ...custom, [key]: e.target.value })}>
        <option value="">{label}…</option>
        {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        {presets.map(p => (
          <Pill key={p.value} active={preset === p.value} onClick={() => onPresetChange(p.value)}>
            {p.label}
          </Pill>
        ))}
        {preset === 'custom' && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {sel('from', 'Kezdő hónap')}
            <span style={{ color: 'var(--text-muted)', fontSize: '0.72rem' }}>…</span>
            {sel('to', 'Záró hónap')}
          </span>
        )}
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      {presets.map(p => (
        <Pill key={p.value} active={preset === p.value} onClick={() => onPresetChange(p.value)}>
          {p.label}
        </Pill>
      ))}
      {preset === 'custom' && (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <input type="date" value={custom?.from || ''} style={dateInputS}
            onChange={e => onCustomChange({ ...custom, from: e.target.value })} />
          <span style={{ color: 'var(--text-muted)', fontSize: '0.72rem' }}>…</span>
          <input type="date" value={custom?.to || ''} style={dateInputS}
            onChange={e => onCustomChange({ ...custom, to: e.target.value })} />
        </span>
      )}
    </div>
  );
}
